//Global imports
import {
    CButton,
    CForm,
    CFormInput,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
} from '@coreui/react';
import { useState, useEffect } from 'react';

//File imports
import { Language } from '../../../../language/English';
import { DocumentCategoryService } from '../../Api/DocumentCategoryService';
import Notification from '../../../../services/Notification/Notification';
import Loader from '../../../../components/Layout/Loader';
import Logger from '../../../../services/Logger/Logger';
import { DOCUMENT_CATEGORY_NAME_MAX_LENGTH } from '../../../../Constant';
import StringUtils from '../../../../services/StringUtils/StringUtils';

//Component imports
import { LengthChecker } from '../../../../components';

interface CreateDocumentCategoryProps {
    stringUtils: StringUtils;
    notification: Notification;
    documentCategoryService: DocumentCategoryService;
    logger: Logger;
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    mode?: 'create' | 'edit';
    categoryData?:
        | {
              documentCategoryPageId?: number;
              name: string;
          }
        | any;
}

interface FormErrors {
    documentGroupPageName?: string;
}

const CreateDocumentCategoryModal: React.FC<CreateDocumentCategoryProps> = ({
    visible,
    onClose,
    onSuccess = () => {},
    mode = 'create',
    categoryData,
    stringUtils,
    notification,
    logger,
    documentCategoryService,
}) => {
    const [formData, setFormData] = useState({
        documentGroupPageName: categoryData?.name || '',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);
    useEffect(() => {
        if (categoryData) {
            setFormData({
                documentGroupPageName: categoryData?.name,
            });
        }
    }, [categoryData]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        let isValid = true;

        if (!formData.documentGroupPageName.trim()) {
            newErrors.documentGroupPageName =
                Language.DOCUMENT_GROUP_PAGE_NAME_REQUIRED;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        setShowLoader(true);
        try {
            let success = false;
            let message = '';
            if (mode === 'create') {
                const response = await documentCategoryService.create(
                    formData.documentGroupPageName,
                );
                success = response.success;
                message = response.message;
            } else if (
                mode === 'edit' &&
                categoryData?.documentCategoryPageId
            ) {
                const response = await documentCategoryService.update(
                    categoryData.documentCategoryPageId,
                    formData.documentGroupPageName,
                );
                success = response.success;
                message = response.message;
            }
            if (success) {
                notification.success(message);
                setFormData({
                    documentGroupPageName: '',
                });
                onClose();
                onSuccess();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    return (
        <CModal visible={visible} onClose={onClose}>
            {showLoader && <Loader />}
            <CModalHeader>
                <CModalTitle>
                    {mode === 'create'
                        ? Language.CREATE_DOCUMENT_GROUP_PAGE
                        : Language.EDIT_DOCUMENT_GROUP_Page}
                </CModalTitle>
            </CModalHeader>
            <CModalBody>
                <CForm onSubmit={handleSubmit}>
                    <div>
                        <CFormInput
                            type="text"
                            name={stringUtils.convertToCamelCase(
                                Language.DOCUMENT_GROUP_PAGE_NAME,
                            )}
                            label={Language.DOCUMENT_GROUP_PAGE_NAME}
                            maxLength={DOCUMENT_CATEGORY_NAME_MAX_LENGTH}
                            placeholder={
                                Language.DOCUMENT_NAME_CATEGORY_PLACEHOLDER
                            }
                            value={formData.documentGroupPageName}
                            onChange={handleChange}
                            invalid={!!errors.documentGroupPageName}
                            feedback={errors.documentGroupPageName}
                        />
                        <LengthChecker
                            currentLength={
                                formData.documentGroupPageName.length
                            }
                            maxLength={DOCUMENT_CATEGORY_NAME_MAX_LENGTH}
                        />
                    </div>
                </CForm>
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" onClick={onClose}>
                    {Language.CANCEL}
                </CButton>
                <CButton color="primary" onClick={handleSubmit}>
                    {mode === 'create'
                        ? Language.CREATE
                        : Language.UPDATE_BUTTON}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default CreateDocumentCategoryModal;
